import React from 'react';
import { Search } from 'lucide-react';
import { DIVERGENCIAS, type CodigoDivergencia, type FiltroStatusSaldos, type FiltrosSaldos, type OrdenacaoSaldos } from './saldosUcs';
import { FAIXA_META } from './saldosUcsVisual';

interface FiltrosSaldosBarProps {
  filtros: FiltrosSaldos;
  onChange: (mudanca: Partial<FiltrosSaldos>) => void;
}

const selectClasse =
  'px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-700 dark:text-slate-200';

/** Barra de filtros da lista de UCs — fica fora da área que rola, então nunca some. A usina é escolhida nos chips do modal. */
const FiltrosSaldosBar: React.FC<FiltrosSaldosBarProps> = ({ filtros, onChange }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col md:flex-row md:items-center gap-2">
      <div className="relative flex-1 min-w-[200px]">
        <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
        <input
          value={filtros.busca}
          onChange={e => onChange({ busca: e.target.value })}
          placeholder="Buscar por UC, nome ou CPF/CNPJ"
          className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-800 dark:text-white"
        />
      </div>
      <select
        value={filtros.status}
        onChange={e => onChange({ status: e.target.value as FiltroStatusSaldos, faixa: null })}
        className={selectClasse}
        aria-label="Status no rateio"
      >
        <option value="recebendo">Recebendo energia</option>
        <option value="nao_recebe">Não recebem (canceladas, sem rateio…)</option>
        <option value="todos">Todas as UCs do rateio</option>
      </select>
      <select
        value={filtros.divergencia}
        onChange={e => onChange({ divergencia: e.target.value as CodigoDivergencia | 'qualquer' | '' })}
        className={selectClasse}
        aria-label="Divergência"
      >
        <option value="">Qualquer situação</option>
        <option value="qualquer">Com alguma divergência</option>
        {(Object.keys(DIVERGENCIAS) as CodigoDivergencia[]).map(d => (
          <option key={d} value={d}>{DIVERGENCIAS[d].rotulo}</option>
        ))}
      </select>
    </div>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => onChange({ soDiferenca: !filtros.soDiferenca })}
          aria-pressed={filtros.soDiferenca}
          title="UCs em que o % sugerido pelo modelo difere do vigente em 0,01 ponto ou mais"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
            filtros.soDiferenca
              ? 'bg-primary/10 border-primary/30 text-primary'
              : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
          }`}
        >
          Só com % diferente do sugerido
        </button>
        {filtros.faixa ? (
          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            Saldo: {filtros.faixa === 'sem_medida' ? 'sem como medir' : FAIXA_META[filtros.faixa].rotulo}
            <button onClick={() => onChange({ faixa: null })} className="ml-1.5 text-gray-400 hover:text-gray-600" aria-label="Limpar faixa">
              ×
            </button>
          </span>
        ) : null}
        {filtros.soAjusteAberto ? (
          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            Só com ajuste em aberto
            <button onClick={() => onChange({ soAjusteAberto: false })} className="ml-1.5 text-gray-400 hover:text-gray-600" aria-label="Limpar filtro de ajuste">
              ×
            </button>
          </span>
        ) : null}
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        Ordenar
        <select
          value={filtros.ordenacao}
          onChange={e => onChange({ ordenacao: e.target.value as OrdenacaoSaldos })}
          className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-gray-700 dark:text-slate-200"
        >
          <option value="cobertura_desc">Mais meses de saldo</option>
          <option value="cobertura_asc">Menos meses de saldo</option>
          <option value="diferenca">Maior diferença para o sugerido</option>
          <option value="saldo_desc">Maior saldo em kWh</option>
          <option value="nome">Nome</option>
        </select>
      </label>
    </div>
  </div>
);

export default FiltrosSaldosBar;
